<template>
  <main class="main h-100 bg-secondary overflow-hidden">
    <div class="container-fluid p-0 h-100 d-none d-lg-block">
      <div
        class="h-100 d-flex"
      >
        <div
          class="h-100 flex-grow-0 flex-shrink-0"
        >
          <app-sidebar
            ref="sidebarEl"
            :current-path="$route.path"
            :base-url="baseUrl"
            :profile-initials="profileInitials"
            :profile-name="profileName"
            :profile-org="companyName"
            @sidebar-navigate="onSidebarNavigate"
          />
        </div>
        <div
          id="dashboard-main-view"
          class="flex-grow-1 p-0 overflow-hidden"
        >
          <div
            v-if="isLoading"
            class="h-100 align-items-center d-flex bg-secondary justify-content-center card-body"
            data-test="dashboard-loading"
          >
            <div class="loader mx-auto" />
          </div>
          <slot v-else />
        </div>
      </div>
    </div>
    <b-container
      fluid
      class="mt-7 d-block d-lg-none"
    >
      <b-row class="justify-content-center">
        <b-col class="col-auto">
          <b-card
            no-body
          >
            <b-card-body>
              <h4 class="text-center my-5">
                This Dashboard must be viewed on a bigger screen
              </h4>
              <p>
                For the best user experience, we have optimised this application
                for screen resolutions of greater than 990 pixels. Please change
                your display settings.
              </p>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </main>
</template>
<script>
import { Auth } from 'aws-amplify';
import { mapActions, mapState, mapGetters } from 'vuex';
import * as authenticationTypes from '@/modules/authentication/store/types';
import * as accountTypes from '@/modules/account/store/types';

export default {
  name: 'BaseDashboard',
  props: {
    isLoading: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      baseUrl: process.env.BASE_URL || '/',
    };
  },
  computed: {
    ...mapState('account', {
      firstName: 'firstName',
      lastName: 'lastName',
      companyName: 'companyName',
    }),
    ...mapState('authentication', {
      roles: 'roles',
    }),
    ...mapGetters('account', [
      accountTypes.GET_PROFILE_PROPERTIES,
    ]),
    profileInitials() {
      const f = this.firstName?.[0]?.toUpperCase() ?? '';
      const l = this.lastName?.[0]?.toUpperCase() ?? '';
      return `${f}${l}`;
    },
    profileName() {
      return [this.firstName, this.lastName].filter(Boolean).join(' ');
    },
  },
  watch: {
    roles: {
      immediate: true,
      handler(roles) {
        this.syncSidebarDomProperties({ roles });
      },
    },
    [accountTypes.GET_PROFILE_PROPERTIES]: {
      immediate: true,
      handler(profileProperties) {
        this.syncSidebarDomProperties({ profileProperties });
      },
    },
  },
  methods: {
    ...mapActions('authentication', [
      authenticationTypes.SIGN_OUT,
    ]),
    ...mapActions('account', [
      accountTypes.FETCH_ACCOUNT,
    ]),
    syncSidebarDomProperties({ roles, profileProperties } = {}) {
      const el = this.$refs.sidebarEl;
      if (!el) return;
      customElements.whenDefined('app-sidebar').then(() => {
        if (!this.$refs.sidebarEl) return;
        if (roles !== undefined) this.$refs.sidebarEl.userRoles = roles;
        if (profileProperties !== undefined) this.$refs.sidebarEl.profileProperties = profileProperties;
      });
    },
    onSidebarNavigate(event) {
      const { path, action } = event.detail;
      if (action === 'signout') {
        this.$router.push({ name: 'signOut' });
      } else if (path?.startsWith('/accounts')) {
        window.location.href = `${process.env.VUE_APP_ACCOUNT_MANAGEMENT_URL}${path}`;
      } else if (path?.startsWith('/campaigns')) {
        window.location.href = `${process.env.VUE_APP_CAMPAIGN_MANAGEMENT_URL}${path}/`;
      } else if (path) {
        this.$router.push(path);
      }
    },
  },
  async created() {
    await this.FETCH_ACCOUNT();
  },
  async mounted() {
    this.syncSidebarDomProperties({
      roles: this.roles,
      profileProperties: this[accountTypes.GET_PROFILE_PROPERTIES],
    });
    try {
      await Auth.currentAuthenticatedUser()
        .catch(() => {
          this.SIGN_OUT()
            .then(() => {
              this.$router.push('/login');
            });
        });
    } catch (e) {
      this.SIGN_OUT()
        .then(() => {
          this.$router.go('/login');
        });
    }
  },
};
</script>
<style lang="scss">
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #47474E;
}
#nav {
  padding: 30px;
  a {
    font-weight: bold;
    color: #47474E;
    &.router-link-exact-active {
      color: #42b983;
    }
  }
}
</style>
